import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ColDef, GridReadyEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import AuthService from '../services/authService';
import Spinner from './Spinner';

export interface TeamAdminsTabRef {
  refresh: () => void;
}

interface MissingAdmin {
  team_id: number;
  team_name: string;
}

interface CreatedAdmin {
  team_id: number;
  team_name: string;
  admin_username: string;
  password: string;
}

interface TeamAdminsSummary {
  total_teams: number;
  onboarded_teams: number;
  admins_created: number;
  admins_not_created: number;
  missing_admins: MissingAdmin[];
  created_admins: CreatedAdmin[];
}

interface CreatedCredentials {
  message?: string;
  team_id: number;
  team_name: string;
  admin_username: string;
  email: string;
  password: string;
}

function getErrorDetail(response: any): string {
  const data = response?.data;
  if (!data) return 'Request failed';

  const username = data.admin_username;
  const detail = data.detail;

  if (typeof detail === 'string') {
    return username ? `${detail} (${username})` : detail;
  }

  if (detail && typeof detail === 'object') {
    const msg = detail.message || detail.detail || 'Request failed';
    const name = detail.admin_username || username;
    return name ? `${msg} (${name})` : String(msg);
  }

  return data.message || 'Request failed';
}

const TeamAdminsTab = forwardRef<TeamAdminsTabRef>((_props, ref) => {
  const navigate = useNavigate();
  const authService = useMemo(() => new AuthService(), []);

  const [summary, setSummary] = useState<TeamAdminsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createdCreds, setCreatedCreds] = useState<CreatedCredentials | null>(null);
  const [creatingTeamId, setCreatingTeamId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [copiedKey, setCopiedKey] = useState('');
  const [pageSize, setPageSize] = useState(20);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await authService.getTeamAdminsSummary();

      if (response.status === 200) {
        setSummary(response.data);
      } else if (response.status === 401 || response.status === 403) {
        navigate('/dashboard');
      } else {
        setError(getErrorDetail(response) || 'Failed to fetch team admins');
      }
    } catch (err) {
      setError('An error occurred while fetching team admins');
    } finally {
      setLoading(false);
    }
  }, [authService, navigate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useImperativeHandle(ref, () => ({
    refresh: fetchSummary,
  }));

  const copyText = useCallback(async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((prev) => (prev === key ? '' : prev)), 1500);
    } catch {
      setError('Failed to copy to clipboard');
    }
  }, []);

  const handleCreate = useCallback(async (team: MissingAdmin) => {
    const confirmed = window.confirm(
      `Create team admin for ${team.team_name} with default password?`
    );
    if (!confirmed) return;

    setError('');
    setCreatedCreds(null);
    setCreatingTeamId(team.team_id);

    try {
      const response = await authService.createTeamAdmin(team.team_id);

      if (response.status === 200 || response.status === 201) {
        setCreatedCreds(response.data);
        await fetchSummary();
      } else if (response.status === 401 || response.status === 403) {
        navigate('/dashboard');
      } else {
        setError(getErrorDetail(response));
      }
    } catch (err) {
      setError('An error occurred while creating the team admin');
    } finally {
      setCreatingTeamId(null);
    }
  }, [authService, fetchSummary, navigate]);

  const filteredMissing = useMemo(() => {
    const rows = summary?.missing_admins || [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.team_name.toLowerCase().includes(q) ||
        String(row.team_id).includes(q)
    );
  }, [summary, search]);

  const filteredCreated = useMemo(() => {
    const rows = summary?.created_admins || [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (row) =>
        row.team_name.toLowerCase().includes(q) ||
        row.admin_username.toLowerCase().includes(q) ||
        String(row.team_id).includes(q)
    );
  }, [summary, search]);

  const missingColumnDefs: ColDef[] = useMemo(
    () => [
      { field: 'team_id', headerName: 'Team ID', filter: true, sortable: true, width: 120 },
      { field: 'team_name', headerName: 'Team Name', filter: true, sortable: true },
      {
        field: 'actions',
        headerName: 'Action',
        sortable: false,
        filter: false,
        width: 140,
        cellRenderer: (params: any) => {
          const team = params.data as MissingAdmin;
          const isPending = creatingTeamId === team.team_id;
          return (
            <button
              onClick={() => handleCreate(team)}
              disabled={creatingTeamId !== null}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-1 px-3 rounded text-xs flex items-center justify-center min-w-[90px] h-[26px]"
            >
              {isPending ? <Spinner size="sm" className="mx-auto" /> : 'Create'}
            </button>
          );
        },
      },
    ],
    [creatingTeamId, handleCreate]
  );

  const createdColumnDefs: ColDef[] = useMemo(
    () => [
      { field: 'team_id', headerName: 'Team ID', filter: true, sortable: true, width: 120 },
      { field: 'team_name', headerName: 'Team Name', filter: true, sortable: true },
      { field: 'admin_username', headerName: 'Username', filter: true, sortable: true },
      { field: 'password', headerName: 'Password', filter: false, sortable: false },
      {
        field: 'copy',
        headerName: 'Copy',
        sortable: false,
        filter: false,
        width: 200,
        cellRenderer: (params: any) => {
          const row = params.data as CreatedAdmin;
          const userKey = `user-${row.team_id}`;
          const passKey = `pass-${row.team_id}`;
          return (
            <div className="flex space-x-2">
              <button
                onClick={() => copyText(userKey, row.admin_username)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-1 px-2 rounded text-xs h-[26px]"
              >
                {copiedKey === userKey ? 'Copied' : 'Username'}
              </button>
              <button
                onClick={() => copyText(passKey, row.password)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-1 px-2 rounded text-xs h-[26px]"
              >
                {copiedKey === passKey ? 'Copied' : 'Password'}
              </button>
            </div>
          );
        },
      },
    ],
    [copiedKey, copyText]
  );

  const defaultColDef = { flex: 1, minWidth: 100, resizable: true };
  const onGridReady = useCallback((params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  }, []);

  const notOnboarded = summary
    ? Math.max(0, (summary.total_teams || 0) - (summary.onboarded_teams || 0))
    : 0;

  const countCards = summary
    ? [
        { label: 'Total teams', value: summary.total_teams },
        { label: 'Onboarded teams', value: summary.onboarded_teams },
        { label: 'Admins created', value: summary.admins_created },
        { label: 'Admins not created', value: summary.admins_not_created },
        { label: 'Not onboarded', value: notOnboarded },
      ]
    : [];

  if (loading && !summary) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded" role="alert">
          {error}
        </div>
      )}

      {createdCreds && (
        <div className="bg-green-100 border border-green-400 text-green-800 px-4 py-3 rounded">
          <p className="font-medium mb-1">{createdCreds.message || 'Team admin created'}</p>
          <p className="text-sm">Team: {createdCreds.team_name}</p>
          <p className="text-sm">Username: {createdCreds.admin_username}</p>
          <p className="text-sm">Email: {createdCreds.email}</p>
          <p className="text-sm">Password: {createdCreds.password}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {countCards.map((card) => (
          <div key={card.label} className="bg-white shadow-md rounded-md p-4">
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">{card.label}</p>
            <p className="text-2xl font-semibold text-gray-800">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Team Admins</h2>
        <input
          type="text"
          placeholder="Search teams..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="p-4 bg-white shadow-md rounded-md overflow-x-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Missing admins</h3>
        <div className="ag-theme-alpine w-full h-[400px]">
          <AgGridReact
            rowData={filteredMissing}
            columnDefs={missingColumnDefs}
            defaultColDef={defaultColDef}
            modules={[AllCommunityModule]}
            animateRows={true}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={pageSize}
            paginationPageSizeSelector={[10, 20, 50, 100]}
            onPaginationChanged={(event) => setPageSize(event.api.paginationGetPageSize())}
            domLayout="normal"
          />
        </div>
        {filteredMissing.length === 0 && (
          <div className="text-center p-8 bg-gray-50 rounded-lg mt-4">
            <p className="text-gray-500">
              {search.trim()
                ? 'No missing admins match the search.'
                : 'All onboarded teams have admins.'}
            </p>
          </div>
        )}
      </div>

      <div className="p-4 bg-white shadow-md rounded-md overflow-x-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Created admins</h3>
        <div className="ag-theme-alpine w-full h-[400px]">
          <AgGridReact
            rowData={filteredCreated}
            columnDefs={createdColumnDefs}
            defaultColDef={defaultColDef}
            modules={[AllCommunityModule]}
            animateRows={true}
            onGridReady={onGridReady}
            pagination={true}
            paginationPageSize={pageSize}
            paginationPageSizeSelector={[10, 20, 50, 100]}
            onPaginationChanged={(event) => setPageSize(event.api.paginationGetPageSize())}
            domLayout="normal"
          />
        </div>
        {filteredCreated.length === 0 && (
          <div className="text-center p-8 bg-gray-50 rounded-lg mt-4">
            <p className="text-gray-500">
              {search.trim()
                ? 'No created admins match the search.'
                : 'No team admins created yet.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

TeamAdminsTab.displayName = 'TeamAdminsTab';

export default TeamAdminsTab;
